import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionItemProps,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  Image,
  ListItem,
  Stack,
  StackDivider,
  Tag,
  TagLabel,
  Text,
  TextProps,
  UnorderedList,
} from '@chakra-ui/react';
import Link from 'next/link';
import React from 'react';

export interface IAgendaEvent {
  id: number | string;
  timing: {
    start: string;
    end?: string;
  };
  labelSubtext?: string;
  accentHeading?: string;
  accentTag?: string;
  normalHeading?: string;
  listContent?: string[];
  speaker?: { name?: string; designation?: string }[];
  speakers?: {
    name?: string;
    designation?: string;
    profileImg?: string;
    link?: string;
  }[];
  listContentWithSub?: {
    title: string;
    subContent: string[];
  }[];
  labelTextColor?: string;
  customSpeakerStyles?: boolean;
  ytLink?: string;
}

interface Props {
  event: IAgendaEvent;
  accordionItemProps?: AccordionItemProps;
  normalHeadingProps?: TextProps;
  labelTextColor?: string;
  adjacentTagVariant?: string;
  customSpeakerStyles?: boolean;
}

const EventCard: React.FC<Props> = ({
  event: {
    timing,
    accentTag,
    accentHeading,
    normalHeading,
    labelSubtext,
    listContent,
    speakers,
    listContentWithSub,
    ytLink,
  },
  accordionItemProps,
  normalHeadingProps,
  labelTextColor = '#56b6a5',
  adjacentTagVariant = 'brandSecondary',
  customSpeakerStyles = false,
}) => (
  <AccordionItem
    py={{ xs: '9px', md: '22px' }}
    px={{ xs: '9px', md: '30px' }}
    bgColor="white"
    border="1px solid #dadafb"
    borderRadius="7px"
    {...accordionItemProps}
  >
    <AccordionButton _hover={{ bgColor: 'white' }}>
      <Stack
        w="full"
        direction={{ xs: 'column', md: 'row' }}
        divider={<StackDivider m="0" color="#dadafb" />}
        userSelect="text"
      >
        <Box minW="112px" mr={{ xs: '0', md: '24px' }} userSelect="text">
          <Flex
            flexDir={{ xs: 'row', md: 'column' }}
            columnGap={{ xs: '4px', md: 0 }}
          >
            <Text
              textAlign="left"
              fontWeight="800"
              fontSize={{ xs: '20px', md: '23px' }}
              lineHeight="130%"
              color="#232343"
              opacity="0.8"
            >
              {timing.start} {timing.end ? '-' : ''}
            </Text>
            {timing.end && (
              <Text
                textAlign="left"
                fontWeight="800"
                fontSize={{ xs: '20px', md: '23px' }}
                lineHeight="130%"
                color="#232343"
                opacity="0.8"
              >
                {timing.end}
              </Text>
            )}
          </Flex>
          {labelSubtext && (
            <Text
              fontWeight="600"
              fontSize="14px"
              lineHeight="140%"
              color="#232343"
              opacity="0.6"
              mt={{ xs: '2px', md: '9px' }}
              textAlign="left"
            >
              {labelSubtext}
            </Text>
          )}
        </Box>
        <Flex
          w="full"
          pl={{ xs: '0', md: '28px' }}
          justifyContent="space-between"
        >
          <Box alignSelf="center">
            {accentHeading && (
              <Text
                fontWeight="800"
                fontSize={{ xs: '13px', md: '16px' }}
                lineHeight="150%"
                color={labelTextColor}
                textAlign="left"
              >
                {accentHeading}
              </Text>
            )}
            {accentTag && (
              <Flex>
                <Tag variant={adjacentTagVariant} size="sm">
                  <TagLabel>{accentTag}</TagLabel>
                </Tag>
              </Flex>
            )}
            {normalHeading && (
              <Text
                fontWeight="800"
                fontSize={{ xs: '16px', md: '19px' }}
                lineHeight="150%"
                color="#3d3d5f"
                textAlign="left"
                mt={accentHeading || accentTag ? '13px' : '0'}
                {...normalHeadingProps}
              >
                {normalHeading}
              </Text>
            )}
            {((listContent && listContent.length > 0) ||
              (speakers && speakers.length > 0)) && (
              <AccordionPanel p="0">
                {listContent && listContent?.length > 0 && (
                  <>
                    <UnorderedList
                      pl={{ xs: '8px', md: '8px' }}
                      mt={{ xs: '4px', md: '8px' }}
                    >
                      {listContent.map((item, index) => (
                        <ListItem
                          key={index}
                          fontWeight="500"
                          fontSize="14px"
                          lineHeight="150%"
                          color="#3d3d5f"
                          opacity="0.78"
                          textAlign="left"
                        >
                          {item}
                        </ListItem>
                      ))}
                    </UnorderedList>
                    {speakers && speakers?.length > 0 && <Divider mt="8px" />}
                  </>
                )}
                {speakers && speakers?.length > 0 && (
                  <UnorderedList
                    pl={{ xs: '8px', md: '8px' }}
                    mt={{ xs: '4px', md: '8px' }}
                  >
                    <Text
                      fontWeight="700"
                      fontSize="14px"
                      lineHeight="150%"
                      color="secondaryPurple.900"
                      opacity="0.6"
                      textAlign="left"
                      mb="12px"
                    >
                      Speakers
                    </Text>
                    {speakers.map((item, index) => (
                      <ListItem
                        key={index}
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="150%"
                        color="#3d3d5f"
                        opacity="0.78"
                        textAlign="left"
                        {...(customSpeakerStyles && {
                          display: 'flex',
                          alignItems: 'start',
                          listStyleType: 'none',
                          _before: {
                            // content: '"•"',
                            color: '#3d3d5f',
                            fontSize: '20px',
                            marginRight: '5px',
                            lineHeight: '2',
                          },
                        })}
                      >
                        <Link href={(item?.link && item?.link) || ''}>
                          <Flex
                            flexDirection={{ xs: 'column', md: 'row' }}
                            alignItems="start"
                            gap="20px"
                            py="8px"
                            border={
                              customSpeakerStyles ? '1px solid #dadafb' : ''
                            }
                            borderRadius="6px"
                            p="8px"
                            boxShadow={customSpeakerStyles ? 'base' : ''}
                            minWidth={{ xs: 'fit-content', lg: '500px' }}
                            my="8px"
                          >
                            {item?.profileImg && (
                              <Image
                                src={item?.profileImg}
                                width="110px"
                                height="110px"
                                borderRadius="6px"
                                objectFit="cover"
                              />
                            )}
                            <Box width="100%">
                              {item.name && (
                                <Text fontWeight="800" as="span">
                                  {item.name},{' '}
                                </Text>
                              )}
                              {item.designation && (
                                <Text as="span">{item.designation}</Text>
                              )}
                            </Box>
                          </Flex>
                        </Link>
                      </ListItem>
                    ))}
                  </UnorderedList>
                )}
                {ytLink && (
                  <Flex alignItems="center" mt="16px">
                    <Link href={ytLink} target="_blank">
                      <Text
                        fontWeight="800"
                        as="span"
                        fontSize={{ xs: '14px', lg: '16px' }}
                      >
                        Youtube:
                      </Text>
                      <Text
                        _hover={{
                          textColor: '#197542',
                        }}
                        as="span"
                        marginLeft="6px"
                        fontSize={{ xs: '14px', lg: '16px' }}
                      >
                        {ytLink}
                      </Text>
                    </Link>
                  </Flex>
                )}
              </AccordionPanel>
            )}
            {listContentWithSub && listContentWithSub.length > 0 && (
              <AccordionPanel p="0">
                <UnorderedList
                  pl={{ xs: '8px', md: '8px' }}
                  mt={{ xs: '4px', md: '8px' }}
                >
                  {listContentWithSub.map((item, index) => (
                    <ListItem
                      key={index}
                      fontWeight="500"
                      fontSize="14px"
                      lineHeight="150%"
                      color="#3d3d5f"
                      opacity="0.78"
                      textAlign="left"
                    >
                      <Text
                        fontWeight="500"
                        fontSize="14px"
                        lineHeight="150%"
                        color="#3d3d5f"
                        opacity="0.78"
                        textAlign="left"
                      >
                        {item?.title}
                      </Text>
                      <UnorderedList
                        pl={{ xs: '8px', md: '8px' }}
                        mt={{ xs: '4px', md: '8px' }}
                      >
                        {item?.subContent?.map((subItem, subIndex) => (
                          <ListItem
                            key={subIndex}
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="150%"
                            color="#3d3d5f"
                            opacity="0.78"
                            textAlign="left"
                          >
                            {subItem}
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </ListItem>
                  ))}
                </UnorderedList>
              </AccordionPanel>
            )}
          </Box>
          {((listContent && listContent.length > 0) ||
            (speakers && speakers.length > 0)) && <AccordionIcon />}
        </Flex>
      </Stack>
    </AccordionButton>
  </AccordionItem>
);

export default EventCard;
